import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';

interface ClientOption {
  id: string;
  name: string;
}

interface NotionUser {
  id: string;
  name: string;
  email?: string | null;
}

interface RoleOption {
  role: string;
  tasks: number;
}

interface RunResult {
  success: boolean;
  project_page_url?: string;
  tasks_created?: number;
  tasks_expected?: number;
  errors?: string[];
}

interface RunRow {
  id: number;
  client_page_id: string;
  client_name: string;
  package?: string;
  task_page_url?: string;
  tasks_created: number;
  status: string;
  created_at: string;
}

type PackageOption = 'Core' | 'Growth' | 'Authority';

const PACKAGES: PackageOption[] = ['Core', 'Growth', 'Authority'];

const parseStreamEvent = (line: string): { log?: string; final?: RunResult } | null => {
  try {
    return JSON.parse(line);
  } catch {
    return null;
  }
};

interface ClientDeliveryAutomationPanelProps {
  renderPackageSelector: (disabled: boolean) => React.ReactNode;
}

const ClientDeliveryAutomationPanel: React.FC<ClientDeliveryAutomationPanelProps> = ({ renderPackageSelector }) => {
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [clientQuery, setClientQuery] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const clientFieldRef = useRef<HTMLDivElement | null>(null);
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [pkg, setPkg] = useState<PackageOption>('Core');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<RunResult | null>(null);
  const [error, setError] = useState('');
  const [logLines, setLogLines] = useState<string[]>([]);
  const logEndRef = useRef<HTMLDivElement | null>(null);

  const [runs, setRuns] = useState<RunRow[]>([]);
  const [deletingRunId, setDeletingRunId] = useState<number | null>(null);

  const [roles, setRoles] = useState<RoleOption[]>([]);
  const [users, setUsers] = useState<NotionUser[]>([]);
  const [roleAssignees, setRoleAssignees] = useState<Record<string, string>>({});
  const [loadingRoles, setLoadingRoles] = useState(false);

  const loadRuns = () =>
    api
      .get('/client-delivery-automation/runs')
      .then((resp) => setRuns(resp.data?.runs || []))
      .catch(() => undefined);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingClients(true);
        const resp = await api.get('/client-delivery-automation/clients');
        if (!cancelled) setClients(resp.data?.clients || []);
      } catch (err: any) {
        if (!cancelled) {
          setError('Failed to load clients: ' + (err.response?.data?.detail || err.message));
        }
      } finally {
        if (!cancelled) setLoadingClients(false);
      }
    })();
    loadRuns();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingRoles(true);
        const resp = await api.get('/client-delivery-automation/roles', { params: { package: pkg } });
        if (cancelled) return;
        setRoles(resp.data?.roles || []);
        setUsers(resp.data?.users || []);
      } catch (err: any) {
        if (!cancelled) {
          setError('Failed to load roles: ' + (err.response?.data?.detail || err.message));
        }
      } finally {
        if (!cancelled) setLoadingRoles(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pkg]);

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (clientFieldRef.current && !clientFieldRef.current.contains(e.target as Node)) {
        setShowClientDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    logEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [logLines]);

  const selectedClient = useMemo(
    () => clients.find((c) => c.id === selectedClientId) || null,
    [clients, selectedClientId]
  );

  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => c.name.toLowerCase().includes(q));
  }, [clients, clientQuery]);

  const assignedCount = roles.filter((r) => roleAssignees[r.role]).length;

  const clientRuns = useMemo(
    () => runs.filter((r) => r.client_page_id === selectedClientId),
    [runs, selectedClientId]
  );

  const handleDeleteRun = async (runId: number) => {
    const confirmed = window.confirm(
      'Archive this delivery project and all of its tasks in Notion?\n' +
      'They go to Notion\'s trash, recoverable for 30 days.'
    );
    if (!confirmed) return;
    setDeletingRunId(runId);
    try {
      const resp = await api.delete(`/client-delivery-automation/run/${runId}`);
      await loadRuns();
      alert(
        `Archived ${resp.data?.archived ?? 0} Notion pages` +
        (resp.data?.failed ? `, ${resp.data.failed} failed` : '')
      );
    } catch (err: any) {
      alert('Delete failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingRunId(null);
    }
  };

  const handleRun = async () => {
    if (!selectedClient) return;
    setRunning(true);
    setResult(null);
    setError('');
    setLogLines([]);
    try {
      const token = localStorage.getItem('auth_token') || '';
      const resp = await fetch('/api/client-delivery-automation/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          client_id: selectedClient.id,
          client_name: selectedClient.name,
          start_date: startDate,
          package: pkg,
          role_assignees: Object.fromEntries(
            roles
              .filter((r) => roleAssignees[r.role])
              .map((r) => [r.role, [roleAssignees[r.role]]])
          ),
        }),
      });
      if (!resp.ok || !resp.body) {
        const text = await resp.text().catch(() => '');
        throw new Error(text || `HTTP ${resp.status}`);
      }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n');
        buffer = parts.pop() || '';
        for (const raw of parts) {
          const line = raw.trim();
          if (!line) continue;
          const evt = parseStreamEvent(line);
          if (typeof evt?.log === 'string') {
            const logLine = evt.log;
            setLogLines((prev) => [...prev, logLine]);
          } else if (evt?.final) {
            setResult(evt.final);
            loadRuns();
          }
        }
      }
    } catch (err: any) {
      setError(err?.message || 'Run failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="max-w-3xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">Onboarding Automation</h2>
      <p className="text-sm text-gray-600 mb-5">
        Pick a client and trigger the Notion onboarding flow — creates the parent project and
        its tasks. The package selects which template is applied.
      </p>

      <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
        {renderPackageSelector(running)}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client package</label>
          <div className="inline-flex rounded-md border border-gray-300 overflow-hidden">
            {PACKAGES.map((p, i) => (
              <button
                key={p}
                type="button"
                onClick={() => setPkg(p)}
                disabled={running}
                className={`px-4 py-2 text-sm font-medium disabled:opacity-50 ${
                  pkg === p ? 'bg-black text-white' : 'bg-white text-gray-700 hover:bg-gray-50'
                } ${i > 0 ? 'border-l border-gray-300' : ''}`}
              >
                {p}
              </button>
            ))}
          </div>
        </div>

        <div ref={clientFieldRef}>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Client {clients.length > 0 && (
              <span className="text-xs font-normal text-gray-400">({clients.length})</span>
            )}
          </label>
          <div className="relative">
            <input
              type="text"
              value={clientQuery}
              onChange={(e) => {
                setClientQuery(e.target.value);
                setShowClientDropdown(true);
                if (selectedClientId !== null) setSelectedClientId(null);
              }}
              onFocus={() => setShowClientDropdown(true)}
              placeholder={loadingClients ? 'Loading clients…' : 'Search clients…'}
              disabled={loadingClients || running}
              className="w-full border border-gray-300 rounded-md px-3 py-2 pr-9 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            {clientQuery && (
              <button
                type="button"
                onClick={() => {
                  setClientQuery('');
                  setSelectedClientId(null);
                  setShowClientDropdown(true);
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700"
                aria-label="Clear"
              >
                ×
              </button>
            )}
            {showClientDropdown && !running && (
              <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                {filteredClients.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">
                    {loadingClients ? 'Loading…' : 'No clients match.'}
                  </div>
                ) : (
                  filteredClients.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setSelectedClientId(c.id);
                        setClientQuery(c.name);
                        setShowClientDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-blue-50 ${
                        c.id === selectedClientId ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-800'
                      }`}
                    >
                      {c.name}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          {clientRuns.length > 0 && !running && (
            <p className="text-xs text-amber-700 mt-1">
              This client already has {clientRuns.length} delivery run{clientRuns.length > 1 ? 's' : ''} below.
              Running again creates another project.
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Project start date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            disabled={running}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <p className="text-xs text-gray-500 mt-1">
            Task dates are the start date plus each template's Start day and End day, counted in working days.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Role assignees{' '}
            {roles.length > 0 && (
              <span className="text-xs font-normal text-gray-400">
                ({assignedCount} of {roles.length} selected)
              </span>
            )}
          </label>
          <p className="text-xs text-gray-500 mb-2">
            The selected person becomes the Assignee or Collaborator on tasks with that role.
            Tasks for an empty role start unassigned.
          </p>
          {loadingRoles && roles.length === 0 ? (
            <div className="text-xs text-gray-500">Loading roles…</div>
          ) : (
            <div className="border border-gray-200 rounded-md divide-y divide-gray-100">
              {roles.map((r) => (
                <div key={r.role} className="px-3 py-2 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                  <div className="sm:w-64 shrink-0 text-sm text-gray-800">
                    {r.role}
                    <span className="ml-1 text-xs text-gray-400">({r.tasks})</span>
                  </div>
                  <select
                    value={roleAssignees[r.role] || ''}
                    onChange={(e) => setRoleAssignees((prev) => ({ ...prev, [r.role]: e.target.value }))}
                    disabled={running}
                    aria-label={`Person for ${r.role}`}
                    className="flex-1 min-w-0 border border-gray-300 rounded px-2 py-1.5 text-sm bg-white disabled:opacity-50"
                  >
                    <option value="">Unassigned</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>{u.name || u.email || u.id}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="pt-2">
          <button
            onClick={handleRun}
            disabled={!selectedClient || running}
            className="px-4 py-2 text-sm font-medium text-white bg-black hover:bg-gray-800 disabled:opacity-50 rounded-md"
          >
            {running ? 'Running…' : 'Run Client Delivery Automation'}
          </button>
        </div>
      </div>

      {(running || logLines.length > 0) && (
        <div className="mt-4">
          <div className="text-xs font-medium text-gray-700 mb-1">Progress</div>
          <div className="bg-gray-900 text-gray-100 text-xs font-mono rounded-md p-3 max-h-72 overflow-y-auto">
            {logLines.length === 0 && running && (
              <div className="text-gray-400">Connecting…</div>
            )}
            {logLines.map((line, i) => (
              <div key={i} className="whitespace-pre-wrap break-words">{line}</div>
            ))}
            <div ref={logEndRef} />
          </div>
        </div>
      )}

      {error && (
        <div className="mt-4 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {result && !result.success && (
        <div className="mt-4 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
          Run failed{result.errors?.[0] ? `: ${result.errors[0]}` : '.'}
        </div>
      )}

      {result?.success && (
        <div
          className={`mt-4 p-4 border rounded-md text-sm ${
            result.errors?.length ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'
          }`}
        >
          <div className={`font-medium mb-2 ${result.errors?.length ? 'text-amber-800' : 'text-green-800'}`}>
            {result.errors?.length ? 'Delivery project created with problems.' : 'Delivery project created.'}
          </div>
          <ul className="text-gray-700 space-y-1">
            {result.project_page_url && (
              <li>
                Project page:{' '}
                <a
                  href={result.project_page_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-700 underline break-all"
                >
                  {result.project_page_url}
                </a>
              </li>
            )}
            <li>Tasks created: {result.tasks_created ?? 0} of {result.tasks_expected ?? 0}</li>
            {result.errors && result.errors.length > 0 && (
              <li className="text-amber-700">Warnings: {result.errors.length} (see progress log)</li>
            )}
          </ul>
        </div>
      )}

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900">Recent runs</h3>
          {runs.length > 0 && <span className="text-xs text-gray-400">{runs.length}</span>}
        </div>
        {runs.length === 0 ? (
          <div className="text-xs text-gray-500 bg-white border border-gray-200 rounded-md px-3 py-3">
            No delivery runs yet. Pick a client above and click Run.
          </div>
        ) : (
          <div className="bg-white border border-gray-200 rounded-md divide-y divide-gray-100">
            {runs.map((r) => (
              <div key={r.id} className="px-3 py-2 flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {r.client_name}
                    {r.package && (
                      <span className="ml-2 text-xs font-normal px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                        {r.package}
                      </span>
                    )}
                    {r.status !== 'completed' && (
                      <span className="ml-2 text-xs text-amber-700 font-normal">({r.status})</span>
                    )}
                  </div>
                  {r.task_page_url ? (
                    <a
                      href={r.task_page_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-blue-700 underline break-all"
                    >
                      {r.task_page_url}
                    </a>
                  ) : (
                    <span className="text-xs text-gray-400">no project URL</span>
                  )}
                  <div className="text-xs text-gray-500 mt-0.5">
                    {r.tasks_created} tasks
                    {r.created_at ? ` · ${new Date(r.created_at).toLocaleString()}` : ''}
                  </div>
                </div>
                <button
                  onClick={() => handleDeleteRun(r.id)}
                  disabled={deletingRunId === r.id}
                  className="shrink-0 px-2.5 py-1 text-xs font-medium text-red-700 bg-white hover:bg-red-50 border border-red-200 rounded disabled:opacity-50"
                  title="Archive in Notion + remove from this list"
                >
                  {deletingRunId === r.id ? 'Deleting…' : 'Delete'}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientDeliveryAutomationPanel;
